import {
  useQuery as useApolloQuery,
  useLazyQuery as useApolloLazyQuery
} from '@apollo/client';
import { useContext, useState, useCallback } from 'react';
import { isAuthorised, AppContext } from '@nile/components';
import { GET_CUSTOMFIELD } from '@nile/banyan/src/Graphql/Mutations/ticket';
import { AUTHORISATION_MAP } from './authorisationMap';
import { AccountClient } from '../client';
import {
  GET_PERSONALIZATION_BY_NAME,
  SAVE_PERSONALIZATION
} from '../Mutation/util';

let transitiontimer = null;

/**
 * @description
 * Custom hooks to override apollo client's hooks.
 * This allows us to handle whatever we need before the API call in a centralised manner.
 * @param {Object} query
 * @param {Object} args
 * @returns {Object}
 */
function useQuery(query, args) {
  const appContext = useContext(AppContext);
  const reference = appContext.user?.role?.roleFeatureList || [];
  const queryName =
    query?.definitions?.[0]?.selectionSet?.selections?.[0]?.name?.value;
  let isQueryAuthorised = true;
  if (queryName && AUTHORISATION_MAP[queryName]) {
    isQueryAuthorised = isAuthorised(reference, AUTHORISATION_MAP[queryName]);
  }
  if (isQueryAuthorised) {
    return useApolloQuery(query, args);
  }
  return {
    loading: false,
    data: { [queryName]: null }
    // error: { message: 'Error..' } if we throw error, it is considered as onError. But the purpose is to skip it. So not throwing error.
  };
}

function useLazyQuery(query, args) {
  return useApolloLazyQuery(query, args);
}

function useCacheQuery(query, args) {
  const [refetchedData, setRefetchedData] = useState(null);
  const allUseQueryProperties = useApolloQuery(query, {
    ...args,
    fetchPolicy: 'cache-and-network'
  });
  const { data, loading } = allUseQueryProperties;
  return {
    ...allUseQueryProperties,
    loading: loading && data === undefined
  };
}

function useCacheQuery1(query, args) {
  const [refetchedData, setRefetchedData] = useState(null);
  const allUseQueryProperties = useApolloQuery(query, {
    ...args,
    fetchPolicy: 'no-cache'
  });
  const { data, loading } = allUseQueryProperties;
  return {
    ...allUseQueryProperties,
    loading: loading && data === undefined
  };
}

function useCachedQuery(query, args) {
  const appContext = useContext(AppContext);
  const reference = appContext.user?.role?.roleFeatureList || [];
  const queryName =
    query?.definitions?.[0]?.selectionSet?.selections?.[0]?.name?.value;
  let isQueryAuthorised = true;
  if (queryName && AUTHORISATION_MAP[queryName]) {
    isQueryAuthorised = isAuthorised(reference, AUTHORISATION_MAP[queryName]);
  }
  if (isQueryAuthorised) {
    return useCacheQuery(query, args);
  }
  return {
    loading: false,
    data: { [queryName]: null }
    // error: { message: 'Error..' } if we throw error, it is considered as onError. But the purpose is to skip it. So not throwing error.
  };
}

function useNoCacheQuery(query, args) {
  const appContext = useContext(AppContext);
  const reference = appContext.user?.role?.roleFeatureList || [];
  const queryName =
    query?.definitions?.[0]?.selectionSet?.selections?.[0]?.name?.value;
  let isQueryAuthorised = true;
  if (queryName && AUTHORISATION_MAP[queryName]) {
    isQueryAuthorised = isAuthorised(reference, AUTHORISATION_MAP[queryName]);
  }
  if (isQueryAuthorised) {
    return useCacheQuery1(query, args);
  }
  return {
    loading: false,
    data: { [queryName]: null }
    // error: { message: 'Error..' } if we throw error, it is considered as onError. But the purpose is to skip it. So not throwing error.
  };
}

/**
 * This is how we have a common method to group most used queries.
 * Just an example. Not used anywhere.
 * @param {Object} args
 * @returns {Object}
 */
function getCustomfield(args) {
  const { module, ALL_MODULES, params } = args;
  return useQuery(GET_CUSTOMFIELD, {
    variables: { id: params.id, module: module || ALL_MODULES.ticket }
  });
}

function savePersonalization(args) {
  return AccountClient.mutate({
    mutation: SAVE_PERSONALIZATION,
    ...args
  });
}

function getPersonalization(args) {
  return AccountClient.query({
    query: GET_PERSONALIZATION_BY_NAME,
    variables: args,
    fetchPolicy: 'network-only'
  });
}

function useCustomTransition() {
  const [isPending, setIsPending] = useState(false);

  // const startTransition = useCallback((callback) => {
  //   setIsPending(true);
  //   window.requestIdleCallback(() => {
  //     callback();
  //     setIsPending(false);
  //   });
  // }, []);

  const startTransition = useCallback((callback) => {
    setIsPending(true);
    if (transitiontimer) {
      clearTimeout(transitiontimer);
    }
    transitiontimer = setTimeout(() => {
      callback();
      setIsPending(false);
    }, 0); // You can adjust the timeout duration as needed
  }, []);

  return [isPending, startTransition];
}

export default useCustomTransition;

export {
  useQuery,
  useLazyQuery,
  getCustomfield,
  useCachedQuery,
  useNoCacheQuery,
  savePersonalization,
  getPersonalization,
  useCustomTransition
};
